import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useStore } from '../store/useStore';
import { getSheetPdfUrl } from '../utils/api';
import styles from './SheetExportModal.module.css';

// Per-sheet export dialog. Two paths:
//   • Without markups → trigger an inline-content-disposition download of
//     the original sheet PDF.
//   • With markups   → open the sheet in the in-app viewer (so all
//     markups render) then immediately fire window.print(). The browser's
//     "Save as PDF" target then captures the page including markups. Not
//     pixel-perfect to a server-rendered export, but it ships in v1
//     without a node-canvas dependency on the client. Phase 2 will add a
//     true client-side rasterized export via pdfjs + canvas + pdf-lib.

export default function SheetExportModal({ sheet, onClose }) {
  const { setActiveSheet, setDrawingsViewMode } = useStore();
  const [withMarkups, setWithMarkups] = useState(false);

  function handleExport() {
    if (!sheet) return;
    if (withMarkups) {
      // Open the viewer for this sheet, then trigger the browser's print
      // dialog. Users pick "Save as PDF" from there. We don't auto-close
      // the modal — let the user re-trigger if their print dialog cancels.
      toast('Opening sheet — use your browser\'s "Save as PDF" from the print dialog.', { duration: 5000, icon: 'i' });
      setActiveSheet(sheet);
      setDrawingsViewMode('viewer');
      onClose();
      // Defer slightly so the viewer mounts before print fires.
      setTimeout(() => {
        try { window.print(); } catch (_) { /* ignore */ }
      }, 600);
      return;
    }
    // Without markups: just hit the existing PDF stream and force download.
    const url = getSheetPdfUrl(sheet.id);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(sheet.sheet_number || 'sheet').replace(/[^\w\-. ]+/g, '_')}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    onClose();
  }

  return (
    <div className={styles.backdrop} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <span>Export sheet</span>
          <button className={styles.closeBtn} onClick={onClose}>×</button>
        </div>
        <div className={styles.body}>
          <div className={styles.sheetLabel}>
            {sheet.sheet_number}{sheet.title ? ` · ${sheet.title}` : ''}
          </div>

          <label className={styles.option}>
            <input
              type="radio"
              name="markups"
              checked={!withMarkups}
              onChange={() => setWithMarkups(false)}
            />
            <div className={styles.optionText}>
              <div className={styles.optionTitle}>Original PDF (no markups)</div>
              <div className={styles.optionSub}>Downloads the source sheet bytes as-is. Fastest.</div>
            </div>
          </label>

          <label className={styles.option}>
            <input
              type="radio"
              name="markups"
              checked={withMarkups}
              onChange={() => setWithMarkups(true)}
            />
            <div className={styles.optionText}>
              <div className={styles.optionTitle}>With markups</div>
              <div className={styles.optionSub}>
                Opens the sheet in the viewer and triggers your browser's
                print dialog. Pick "Save as PDF" to capture the markups.
              </div>
            </div>
          </label>
        </div>
        <div className={styles.footer}>
          <button className={styles.btnSecondary} onClick={onClose}>Cancel</button>
          <button className={styles.btnPrimary} onClick={handleExport}>Export</button>
        </div>
      </div>
    </div>
  );
}
